import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InventoryId from '../../../components/Icons/InventoryId';
import InventoryCount from '../../../components/Icons/InventoryCount';
import BackIcon from '../../../components/Icons/BackIcon';
import WrenchIcon from '../../../components/Icons/WrenchIcon';
import DocumentIcon from '../../../components/Icons/DocumentIcon';
import { useStartManagingInventoryRequest, useEndManagingInventoryRequest } from '../../../hooks/useApi';
import { InUseModal } from './InUseModal';
import './Header.css';

export const Header = ({inventory, posId, isHqUser}) =>
{
  const navigate = useNavigate();
  const [showInUseModal, setShowInUseModal] = useState(false);
  const [inUseUser, setInUseUser] = useState('');
  const [killingSession, setKillingSession] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [{loading: startingManaging}, startManagingInventory] = useStartManagingInventoryRequest(posId);
  const [, endManagingInventory] = useEndManagingInventoryRequest(posId);

  const formatDate = (dateString) =>
  {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit', year: 'numeric'});
  };

  const getStatusClass = (status) =>
  {
    switch (status)
    {
      case 'ACTIVE':
      case 'IN_REVIEW':
        return 'status-active';
      case 'COMPLETED':
        return 'status-completed';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return 'status-default';
    }
  };

  const handleManageDiscrepancies = async () =>
  {
    try
    {
      const response = await startManagingInventory({
        bodyParams: { inventoryId: parseInt(inventory.id, 10) }
      });

      console.log('Start managing response:', response);

      // started: true means inventory is already being managed by someone else
      // started: false (or null currentUser) means we successfully started managing
      if (response?.data?.started === true && response?.data?.currentUser)
      {
        const currentUser = response?.data?.currentUser;
        setInUseUser(currentUser);
        setShowInUseModal(true);
        return; // Don't navigate
      }

      // If started: false or currentUser is null, we can proceed with navigation
      navigate(`/inventory-management/${inventory.id}/manage-discrepancies`);
    }
    catch (error)
    {
      console.error('Error starting to manage inventory:', error);
      alert('Failed to start managing inventory. Please try again.');
    }
  };

  const handleKillSession = async () =>
  {
    setKillingSession(true);
    try
    {
      await endManagingInventory({
        bodyParams: { inventoryId: parseInt(inventory.id, 10) }
      });
    }
    catch (error)
    {
      console.error('Error ending inventory session:', error);
    }

    try
    {
      await startManagingInventory({
        bodyParams: { inventoryId: parseInt(inventory.id, 10) }
      });
    }
    catch (error)
    {
      console.error('Error starting managing after kill:', error);
    }

    setKillingSession(false);
    setShowInUseModal(false);
    navigate(`/inventory-management/${inventory.id}/manage-discrepancies`);
  };

  const handleViewReport = () =>
  {
    navigate(`/inventory-management/${inventory.id}/report`);
  };

  if (!inventory)
  {
    return null;
  }


  const isCompleted = inventory.status === 'COMPLETED';
  const hasSessions = (inventory.totalSessions || 0) > 0;
  const canManage = hasSessions && (
    inventory.status === 'ACTIVE'
    || inventory.status === 'REJECTED'
    || (inventory.status === 'IN_REVIEW' && isHqUser)
  );

  return (
    <div className="inv-detail-header">
      {/* Left side: all info */}
      <div className="inv-detail-header-left">
        {/* Back button, title and status */}
        <div className="inv-detail-header-top">
          <button
            onClick={() => navigate('/inventory-management')}
            className="inv-detail-back-btn"
          >
            <BackIcon/>
          </button>
          <div className="inv-detail-title-section">
            <h1 className="inv-detail-title">{inventory.name}</h1>
            <span className={`inv-detail-status ${getStatusClass(inventory.status)}`}>
                  <span className="status-dot"></span>
              {inventory.status}
            </span>
          </div>
        </div>

        {/* Inventory info row */}
        <div className="inv-detail-info">
          <div className="inv-detail-info-row">
            <div className="inv-header-left">
              <div className="inv-creation-details">
                <div className="inv-detail-info-item">
                  <InventoryId width={16} height={16} color={'#717182'}/>
                  <span className="inv-detail-info-label">ID:</span>
                  <span className="inv-detail-info-value">{inventory.id}</span>
                </div>
                <span className="divider"></span>
                <div className="inv-detail-info-item">
                  <InventoryCount width={16} height={16}/>
                  <span className="inv-detail-info-label">Total:</span>
                  <span className="inv-detail-info-value">{inventory.expectedQuantity || 0} Pcs</span>
                </div>
              </div>
              <div className="inv-detail-info-item">
                <span className="inv-detail-info-label">Created:</span>
                <span
                  className="inv-detail-info-value">{formatDate(inventory.createdOn)} by {inventory.createdBy}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: season tags */}
      {inventory.filters && inventory.filters.length > 0 && (
        <div className="inv-detail-tags">
          {(tagsExpanded ? inventory.filters : inventory.filters.slice(0, 3)).map((filter, idx) => (
            <span key={idx} className="inv-detail-tag">
              {filter.type}: {filter.value}
            </span>
          ))}
          {inventory.filters.length > 3 && (
            <button
              className="inv-detail-tags-toggle"
              onClick={() => setTagsExpanded((v) => !v)}
            >
              {tagsExpanded ? 'Show less' : `Show more +${inventory.filters.length - 3}`}
            </button>
          )}
        </div>
      )}
      {isCompleted ? (
        <button
          className="inv-action-btn"
          onClick={handleViewReport}
        >
          <DocumentIcon width={20} height={20} color={'#fff'}/>
          View Inventory Report
        </button>
      ) : (
        <button
          className="inv-action-btn"
          onClick={handleManageDiscrepancies}
          disabled={!canManage || startingManaging}
        >
          <WrenchIcon width={20} height={20} color={'#fff'}/>
          {startingManaging ? 'Starting...' : 'Review & Manage Discrepancies'}
        </button>
      )}

      <InUseModal
        isOpen={showInUseModal}
        onClose={() => setShowInUseModal(false)}
        userName={inUseUser}
        isHqUser={isHqUser}
        onKillSession={handleKillSession}
        killingSession={killingSession}
      />
    </div>
  );
};
